import apiClient from '../helpers/apiClient'


export interface ChangeGlobalPayload {
  newValue: string
  effectiveFrom: string     // ISO datetime
  reason: string
  idempotencyKey: string
}

export interface SetOverridePayload {
  scope: 'partner' | 'experience' | 'engagement'
  targetId: number
  newValue: string | null    // null = clear
  effectiveFrom?: string
  reason: string
  idempotencyKey: string
}

export interface CancelScheduledPayload {
  reason: string
  idempotencyKey: string
}

const asSnake = (v: string) =>
  v.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)

const toSnake = <T extends object>(obj: T): Record<string, any> => {
  const out: Record<string, any> = {}
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined) out[asSnake(k)] = v
  })
  return out
}


export const getGlobalRate = async () => {
  const response = await apiClient.get('/commissions/global/')
  return response.data
}

export const changeGlobalRate = async (payload: ChangeGlobalPayload) => {
  const response = await apiClient.post('/commissions/global/', toSnake(payload))
  return response.data
}

export const getPartnerOverrides = async (params: { hasOverride?: string; search?: string } = {}) => {
  const query: Record<string, any> = {
    has_override: params.hasOverride,
    search: params.search,
  }
  Object.keys(query).forEach((k) => (query[k] === undefined || query[k] === '') && delete query[k])
  const response = await apiClient.get('/commissions/overrides/partners/', { params: query })
  return response.data
}

export const getItemOverrides = async (params: {
  itemType?: 'experience' | 'engagement'
  hasOverride?: string
  search?: string
} = {}) => {
  const query: Record<string, any> = {
    item_type: params.itemType ?? 'experience',
    has_override: params.hasOverride,
    search: params.search,
  }
  Object.keys(query).forEach((k) => (query[k] === undefined || query[k] === '') && delete query[k])
  const response = await apiClient.get('/commissions/overrides/items/', { params: query })
  return response.data
}

export const setOverride = async (payload: SetOverridePayload) => {
  const body = {
    scope: payload.scope,
    target_id: payload.targetId,
    new_value: payload.newValue,
    effective_from: payload.effectiveFrom,
    reason: payload.reason,
    idempotency_key: payload.idempotencyKey,
  }
  const response = await apiClient.post('/commissions/overrides/', body)
  return response.data
}

export const getHistory = async (params: {
  scope?: string
  targetId?: number
  dateFrom?: string
  dateTo?: string
  limit?: number
  offset?: number
} = {}) => {
  const query: Record<string, any> = {
    scope: params.scope,
    target_id: params.targetId,
    date_from: params.dateFrom,
    date_to: params.dateTo,
    limit: params.limit,
    offset: params.offset,
  }
  Object.keys(query).forEach((k) => (query[k] === undefined || query[k] === '') && delete query[k])
  const response = await apiClient.get('/commissions/history/', { params: query })
  return response.data
}

export const getScheduled = async () => {
  const response = await apiClient.get('/commissions/scheduled/')
  return response.data
}

export const cancelScheduled = async (id: number, payload: CancelScheduledPayload) => {
  const response = await apiClient.post(`/commissions/scheduled/${id}/cancel/`, toSnake(payload))
  return response.data
}
